import { EventDefinitions, type EventPayloads } from "./types";
import { validatePayload } from "./validatePayload";

type Track<T extends keyof EventPayloads> = {
  eventName: T;
  payload: EventPayloads[T];
};

const queue: Array<Track<keyof EventPayloads>> = [];
let interval: NodeJS.Timeout | undefined;

const enqueue = <T extends keyof EventPayloads>({
  eventName,
  payload,
}: Track<T>): void => {
  queue.push({ eventName, payload });

  if (!interval) {
    interval = setInterval(() => {
      if (typeof window !== "undefined" && window.tagular) {
        clearInterval(interval);
        queue.forEach(({ eventName, payload }) => {
          if (window.tagular) {
            window.tagular(eventName, payload);
          }
        });
        // Clear the queue after processing
        queue.length = 0;
      }
    }, 300);
  }
};

export const tagular = <T extends keyof EventPayloads>(
  eventName: T,
  payload: EventPayloads[T]
): void => {
  if (!validatePayload(eventName, payload)) {
    return;
  }

  const event = EventDefinitions[eventName].eventType;
  if (!event) {
    console.error(`${eventName} is not a supported event`);
    return;
  }

  // Create a deep copy of the payload with uppercased string values
  const uppercasedPayload = JSON.parse(
    JSON.stringify(payload)
  ) as EventPayloads[T];

  // Recursively uppercase string values in the payload
  const uppercaseStrings = (obj: any): any => {
    if (typeof obj === "string") {
      return obj.toUpperCase();
    } else if (Array.isArray(obj)) {
      return obj.map(item => uppercaseStrings(item));
    } else if (obj && typeof obj === "object") {
      const result: any = {};
      for (const key in obj) {
        result[key] = uppercaseStrings(obj[key]);
      }
      return result;
    }
    return obj;
  };

  // Apply the uppercase transformation
  const transformedPayload = uppercaseStrings(
    uppercasedPayload
  ) as EventPayloads[T];

  const body = {
    "@type": event,
    ...transformedPayload,
  };

  if (typeof window !== "undefined" && window.tagular) {
    try {
      window.tagular("beam", body);
    } catch (e) {
      console.error("Tagular:", e);
    }

    if (window._env_.FRONTEND_ENV !== "prod") {
      console.groupCollapsed(
        `%cTagular: ${event}`,
        "background: #2980b9; color: #fff; padding: 3px 6px;"
      );
      Object.entries(body).forEach(([key, value]) => {
        console.log(`%c${key}:`, "color: #2980b9", value);
      });
      console.groupEnd();
    }
  } else {
    console.warn("Tagular: Not found in window");
    enqueue({ eventName, payload: transformedPayload });
  }
};
